// The three pieces that make /accounts/:accountId/... work without stranding anybody.
//
// All three answer one question the router cannot: WHICH account. The URL names it
// everywhere except at the two moments it cannot — a fresh login, and a bookmark saved
// before the segment existed — and both have to resolve it from the server rather than
// guess. The id is 20 random characters, so there is no value a client could construct.
import { Navigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AppShell, ShellSkeleton, firstAllowedPath } from "@/components/shell/AppShell";
import { apiFetch } from "@/lib/api-client";
import { useAccount, type Identity } from "@/providers/AccountProvider";

/** Ask the server which account this person lands in. Sends no account header: the server
 *  answers with their own, which is the right default for somebody who has not chosen. */
function useLanding() {
  return useQuery({
    queryKey: ["team", "landing"],
    queryFn: () => apiFetch<Identity>("/api/team/me"),
    staleTime: 60_000,
    retry: false,
  });
}

/**
 * Where a session with no account in the URL goes.
 *
 * Resolved, never guessed — picking the wrong account would show somebody an empty
 * workspace, which reads as data loss rather than as a routing mistake.
 */
export function AccountLanding() {
  const { data, isLoading, isError } = useLanding();
  if (isLoading) return <ShellSkeleton />;
  // A failure here means the session is bad or the API is down. /login is honest for the
  // first and at least explains itself for the second, unlike a shell whose every panel
  // errors separately.
  if (isError || !data?.account_id) return <Navigate to="/login" replace />;
  return (
    <Navigate
      to={`/accounts/${data.account_id}${firstAllowedPath(data.permissions)}`}
      replace
    />
  );
}

/**
 * `/accounts/<id>` with nothing after it: resolve where in THAT account to stand.
 *
 * The switcher navigates here rather than to a screen it picked, because the permissions
 * that decide the answer belong to the account being switched INTO — and the client
 * cannot know them until it has asked as that account. Rendering inside AccountProvider
 * is what makes the question answerable: by the time this runs, identity has been
 * refetched with the new account's header.
 */
export function AccountIndexRedirect() {
  const { identity, isLoading } = useAccount();
  // NOT ShellSkeleton. This renders inside AppShell's <Outlet>, so the rail and panel are
  // already on screen — a skeleton here draws a second rail inside <main>. Every other
  // ShellSkeleton call site replaces the whole page, before AppShell exists.
  if (isLoading) {
    return <p className="px-6 py-16 text-center text-[13px] text-n-fg-faint">Loading…</p>;
  }
  if (!identity) return <Navigate to="/login" replace />;
  return <Navigate to={firstAllowedPath(identity.permissions)} replace />;
}

/**
 * A path that predates the account segment, forwarded WITH whatever followed it.
 *
 * `/contacts/FuTcMMVrNeIFoGyrlFRA` must arrive at
 * `/accounts/<theirs>/contacts/FuTcMMVrNeIFoGyrlFRA`. Dropping the tail and landing on the
 * dashboard would be worse than a 404: it looks like the link worked, and quietly shows
 * something else.
 */
export function LegacyPathRedirect() {
  const location = useLocation();
  const { data, isLoading, isError } = useLanding();
  if (isLoading) return <ShellSkeleton />;
  if (isError || !data?.account_id) return <Navigate to="/login" replace />;
  const tail =
    location.pathname === "/" ? firstAllowedPath(data.permissions) : location.pathname;
  return <Navigate to={`/accounts/${data.account_id}${tail}${location.search}`} replace />;
}

/**
 * The app chrome, one level inside the account segment.
 *
 * Its own component because AppShell is a LAYOUT rendering an <Outlet>, and it now sits
 * inside AccountProvider — which must itself be inside the route that owns :accountId.
 * Wrapping AppShell directly in App.tsx would put the provider above the param it reads.
 */
export function ShellOutlet({ onOpenPalette }: { onOpenPalette: () => void }) {
  return <AppShell onOpenPalette={onOpenPalette} />;
}
